import { FormsModule } from '@angular/forms';
import { JsonPipe } from '@angular/common';
import { Component, inject, input, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { differenceInCalendarDays } from 'date-fns';
import { filter, first } from 'rxjs';
import { tap } from 'rxjs/operators';
import { collection, doc, docData, Firestore, getDocs } from '@angular/fire/firestore';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzCollapseComponent, NzCollapsePanelComponent } from 'ng-zorro-antd/collapse';
import { NzDatePickerComponent, NzRangePickerComponent } from 'ng-zorro-antd/date-picker';

import { AzureConnectorService } from '../../services/azure-connector.service';
import { environment } from '../../../../../environments/environment';
import { FormService } from '../../services/form.service';
import { FormValues } from '@aaa/interface-agentScheduler';
import { JwtAuthService } from '../../../../services/jwt-auth';

interface Tools {
  info: {
    roles: string[];
    enabled: boolean;
    projectId: string;
    endpointUrl: string;
  };
  reports: {
    roles: string[];
    enabled: boolean;
    today: Date;
    range: {
      start: string | null;
      end: string | null;
    };
  };
  connection: {
    roles: string[];
    enabled: boolean;
    loading: boolean;
    results:
      | string
      | {
          email: string;
          status: number | undefined;
          message: string | undefined;
        }[];
    appId: string | undefined;
  };
  hours: {
    roles: string[];
    enabled: boolean;
    loading: boolean;
    results:
      | string
      | {
          email: string;
          source: string;
          timezone: string;
          hours: string;
        }[];
  };
  fnConfig: {
    roles: string[];
    enabled: boolean;
    setup: () => Promise<void>;
    doc: string;
  };
}

@Component({
  selector: 'ava-tools',
  standalone: true,
  templateUrl: './tools.component.html',
  imports: [
    NzButtonComponent,
    NzCollapseComponent,
    NzCollapsePanelComponent,
    NzRangePickerComponent,
    FormsModule,
    NzDatePickerComponent,
    JsonPipe,
  ],
})
export class ToolsComponent implements OnInit {
  private http = inject(HttpClient);
  acs = inject(AzureConnectorService);
  firestore = inject(Firestore);
  formService = inject(FormService);
  jwtAuthService = inject(JwtAuthService);

  formId = input('');

  formValues?: FormValues;
  today = new Date();
  date?: string;
  showTools = false;
  showAccordion = false;
  tools: Tools = {
    info: {
      roles: ['administrator', 'webmaster'],
      enabled: false,
      projectId: environment.firebaseConfig?.projectId,
      endpointUrl: environment.cloudFunctionsURL,
    },
    reports: {
      roles: ['administrator', 'webmaster'],
      enabled: false,
      today: new Date(),
      range: {
        start: null,
        end: null,
      },
    },
    connection: {
      roles: ['administrator', 'webmaster'],
      enabled: false,
      loading: false,
      results: '',
      appId: '',
    },
    hours: {
      roles: ['administrator', 'webmaster'],
      enabled: false,
      loading: false,
      results: [],
    },
    fnConfig: {
      roles: ['administrator', 'webmaster'],
      enabled: false,
      setup: this.setupFnConfig.bind(this),
      doc: '',
    },
  };

  constructor() {
    this.jwtAuthService.afAuthIdTokenResult$
      .pipe(
        takeUntilDestroyed(),
        filter((afAuthIdTokenResult) => Boolean(afAuthIdTokenResult?.claims?.roles?.length)),
        tap((afAuthIdTokenResult) => this.updateToolsBasedOnUserRoles(afAuthIdTokenResult?.claims.roles)),
      )
      .subscribe();
  }

  ngOnInit() {
    if (this.formId()) {
      this.formValues = this.formService.formValues[this.formId()];
    }
  }

  private updateToolsBasedOnUserRoles(userRoles: string[] | undefined): void {
    const toolsSections = [
      this.tools.info,
      this.tools.reports,
      this.tools.connection,
      this.tools.hours,
      this.tools.fnConfig,
    ];

    for (const section of toolsSections) {
      section.enabled = section.roles.some((sectionRole) =>
        userRoles?.some((userRole: string) => userRole.toLowerCase() === sectionRole.toLowerCase()),
      );

      if (section.enabled && 'setup' in section) {
        section.setup().then();
      }

      this.showTools = this.showTools || section.enabled;
    }
  }

  async connectionAgentsRefresh(): Promise<void> {
    if (this.tools.connection.loading) return;
    this.tools.connection.loading = true;
    this.tools.connection.results = 'refreshing agent availability. this can take a while for larger clubs...';
    this.tools.connection.appId = 'getting azure app ID...';
    const availabilityRefresh = await this.acs.availabilityRefresh(
      this.formValues?.meta.clubId || '',
      'tools component',
      this.formValues?.meta.tenantId || '',
      this.formValues?.agents.map((agent) => agent.email) || [],
      true,
    );
    this.tools.connection.results = availabilityRefresh.map((document) => ({
      email: document.agentEmail,
      status: document.status,
      message: document.message,
    }));
    this.tools.connection.appId = await this.acs.statusAzureApp(this.formValues?.meta.clubId || '');
    this.tools.connection.loading = false;
  }

  async hoursGetAgentHours(): Promise<void> {
    if (this.tools.hours.loading) return;
    this.tools.hours.loading = true;
    this.tools.hours.results = 'getting agent work hours...';
    const clubId = this.formValues?.meta.clubId;
    if (clubId) {
      // const workHoursRef = this.afs.firestore
      //   .collection('wss-aaa-web')
      //   .doc(this.formValues?.meta.clubId)
      //   .collection('apps')
      //   .doc('agent-scheduler')
      //   .collection('data')
      //   .doc('gcf')
      //   .collection('work-hours');
      //
      // const workHoursDocs = await workHoursRef.get();
      // Todo: need to test
      const workHoursCollection = collection(
        this.firestore,
        `wss-aaa-web/${this.formValues?.meta.clubId}/apps/agent-scheduler/data/gcf/work-hours`,
      );
      const workHoursDocs = await getDocs(workHoursCollection);
      if (!workHoursDocs.empty) {
        const results: Tools['hours']['results'] = [];
        workHoursDocs.forEach((doc) => {
          const data = doc.data();
          if (doc.id === '000index') {
            return;
          }
          results.push({
            email: data.agentEmail,
            source: data.source,
            timezone: data.timezone,
            hours: data.hours,
          });
        });
        this.tools.hours.results = results;
      } else {
        this.tools.hours.results = 'failed';
      }
    }
    this.tools.hours.loading = false;
  }

  async reportsDownload(): Promise<void> {
    if (!this.tools.reports.range.start || !this.tools.reports.range.start) return;

    const url =
      environment.cloudFunctionsURL +
        '/agentSchedulerReport?fmt=csv&type=travel&clubId=' +
        this.formValues?.meta.clubId ||
      '' + '&start=' + this.tools.reports.range.start ||
      '' + '&end=' + this.tools.reports.range.end ||
      '';

    // const afAuthIdTokenResult = this.globalState.get('afAuthIdTokenResult');
    this.jwtAuthService.afAuthIdTokenResult$.pipe(first()).subscribe(async (afAuthIdTokenResult) => {
      const afAuthIdToken = afAuthIdTokenResult?.token || '';
      const headers = new HttpHeaders({ Authorization: 'Bearer ' + afAuthIdToken });
      const resp: HttpResponse<Blob> | undefined = await this.http
        .get<Blob>(url, {
          headers: headers,
          observe: 'response',
          responseType: 'blob' as 'json',
        })
        .toPromise();
      const filename = resp?.headers.get('content-disposition')?.split('"')[1];
      if (resp?.body && filename) {
        this.downloadFile(resp.body, filename, 'application/ms-excel');
      }
    });
  }

  downloadFile(data: Blob, filename: string, type: string): void {
    const blob = new Blob([data], { type: type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click(); //unreal
    window.URL.revokeObjectURL(url);
  }

  datePickerRange(event: Date[]): void {
    const dateRange: (string | null)[] = [null, null];
    if (event.length === 2) {
      dateRange[0] = event[0].toISOString()?.split('T')[0];
      dateRange[1] = event[1].toISOString()?.split('T')[0];
    }
    this.tools.reports.range.start = dateRange[0];
    this.tools.reports.range.end = dateRange[1];
  }

  async setupFnConfig(): Promise<void> {
    const clubId = this.formValues?.meta.clubId;

    if (clubId) {
      // this.afs
      //   .collection('wss-aaa-web')
      //   .doc(clubId)
      //   .collection('apps')
      //   .doc('agent-scheduler')
      //   .valueChanges()
      //   .subscribe((item) => {
      //     this.tools.fnConfig.doc = JSON.stringify(item, null, 2);
      //     // console.log(item);
      //   });
      // Todo: need to test
      const appDocRef = doc(this.firestore, `wss-aaa-web/${clubId}/apps/agent-scheduler`);
      docData(appDocRef).subscribe((item) => {
        this.tools.fnConfig.doc = JSON.stringify(item, null, 2);
      });
    }
  }

  datePickerDisabledDates = (current: Date): boolean => differenceInCalendarDays(current, this.today) > 0;
}
